import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FrameworkService, FrameworkdataService } from 'gadoe-framework';
import {
  ICalculatorConfigViewModel,
  ICalculatorSearchView,
  ICalculatorSearchViewModel,
  ISchoolsView,
  ISystemsView
} from '../models/common';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Config } from '../../../app/shared/config';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private calcSearchUrl = '/CalcSearch';

  constructor(
    private http: HttpClient,
    private frameworkService: FrameworkService,
    private frameworkDataService: FrameworkdataService
  ) { }

  getCalculatorConfig(): Observable<ICalculatorConfigViewModel> {
    const url = Config.ROOT_URL + this.calcSearchUrl + '/GetCalculatorConfig';
    this.frameworkService.showSpinner();

    return this.http.get<ICalculatorConfigViewModel>(url, { headers: Config.httpHeaders }).pipe(
      finalize(() => this.frameworkService.hideSpinner()),
      catchError((error) => this.handleError(error))
    );
  }

  getSystemsList(): Observable<ISystemsView[]> {
    const url = Config.ROOT_URL + this.calcSearchUrl + '/GetSystemsList';
    this.frameworkService.showSpinner();

    return this.http.get<ISystemsView[]>(url, { headers: Config.httpHeaders }).pipe(
      finalize(() => this.frameworkService.hideSpinner()),
      catchError((error) => this.handleError(error))
    );
  }

  getSchoolsList(systemID: string): Observable<ISchoolsView[]> {
    const url = Config.ROOT_URL + this.calcSearchUrl + '/GetSchoolsList?SystemID=' + systemID;
    this.frameworkService.showSpinner();

    return this.http.get<ISchoolsView[]>(url, { headers: Config.httpHeaders }).pipe(
      finalize(() => this.frameworkService.hideSpinner()),
      catchError((error) => this.handleError(error))
    );
  }

  getCalcSearchData(calcSearch: ICalculatorSearchView): Observable<ICalculatorSearchViewModel[]> {
    const url = Config.ROOT_URL + this.calcSearchUrl + '/GetCalculatorSearch';
    const urlWithParams = `${url}?${this.buildCalcSearchParams(calcSearch)}`;
    this.frameworkService.showSpinner();

    return this.http.get<ICalculatorSearchViewModel[]>(urlWithParams, { headers: Config.httpHeaders }).pipe(
      finalize(() => this.frameworkService.hideSpinner()),
      catchError((error) => this.handleError(error))
    );
  }

  handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      return observableThrowError(() => error.error.message);
    }

    return observableThrowError(() => error);
  }

  private buildCalcSearchParams(calcSearch: ICalculatorSearchView): string {
    const params = new URLSearchParams();

    params.set('fiscalYear', String(calcSearch.fiscalYear ?? ''));
    params.set('systemID', calcSearch.systemID ?? '');
    params.set('schoolID', calcSearch.schoolID ?? '');
    params.set('firstName', calcSearch.firstName ?? '');
    params.set('lastName', calcSearch.lastName ?? '');
    params.set('dateofBirth', this.formatDateOfBirth(calcSearch.dateofBirth));

    return params.toString();
  }

  private formatDateOfBirth(value: Date | string): string {
    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  }
}
